import QuickBooksSourceBanner from './QuickBooksSourceBanner';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  TableSortLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import quickbooksService, {
  formatCurrency,
  formatCurrencyPrecise,
  formatCompactCurrency,
  formatDate,
  formatRelativeDate,
  calculateDaysOverdue,
  getAgingColor,
  getEntityColor,
} from '../../services/quickbooksService';
import arApNativeService from '../../services/arApNativeService';

export default function APAnalysis() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [apAging, setApAging] = useState(null);
  const [bills, setBills] = useState([]);
  const [vendorBalances, setVendorBalances] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('due_date');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadData();
  }, [selectedEntity]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = selectedEntity !== 'all' ? { connector_id: selectedEntity } : {};

      const [entitiesRes, apRes, billsRes, vendorsRes] = await Promise.all([
        arApNativeService.getEntities(),
        arApNativeService.getAPAgingSummary(params),
        arApNativeService.getOutstandingBills({ ...params, limit: 100 }),
        arApNativeService.getVendorBalances({ ...params, limit: 50 }),
      ]);

      if (entitiesRes.success) setEntities(entitiesRes.data);
      if (apRes.success) setApAging(apRes.data);
      if (billsRes.success) setBills(billsRes.data.bills || []);
      if (vendorsRes.success) setVendorBalances(vendorsRes.data || []);

    } catch (err) {
      console.error('Error loading AP data:', err);
      setError('Failed to load accounts payable data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (event) => {
    setSelectedEntity(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const filteredBills = bills
    .filter(bill =>
      (bill.vendor_ref_name || bill.vendor_name || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bill.doc_number || '')?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'due_date' || sortBy === 'txn_date') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }
      if (sortBy === 'balance' || sortBy === 'total_amt') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalAP = apAging?.total || 0;
  const reportingCurrency = apAging?.reporting_currency || 'USD';
  const overdueAmount = apAging?.overdue_amount || 0;
  const overdueCount = apAging?.overdue_count || 0;

  return (
    <Box sx={{ p: 3 }}>
      <QuickBooksSourceBanner />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Accounts Payable Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Outstanding bills and vendor balances
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={selectedEntity}
              label="Entity"
              onChange={handleEntityChange}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.connector_id} value={entity.connector_id}>
                  {entity.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Payables
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {formatCompactCurrency(totalAP, reportingCurrency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {bills.length} open bills
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'warning.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <PaymentIcon sx={{ color: 'warning.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Overdue Amount
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {formatCompactCurrency(overdueAmount, reportingCurrency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {overdueCount} overdue bills
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'error.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <WarningAmberIcon sx={{ color: 'error.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Active Vendors
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {vendorBalances.length}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'info.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <BusinessIcon sx={{ color: 'info.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Overdue %
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      color: overdueAmount / totalAP > 0.3 ? 'error.main' : 'success.main'
                    }}
                  >
                    {totalAP > 0 ? ((overdueAmount / totalAP) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Aging Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Aging Breakdown
          </Typography>
          <Grid container spacing={2}>
            {apAging?.buckets && Object.entries(apAging.buckets).map(([bucket, amount]) => (
              <Grid item xs={12} sm={6} md={2.4} key={bucket}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{bucket}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(amount, reportingCurrency)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAP > 0 ? (amount / totalAP) * 100 : 0}
                    color={getAgingColor(bucket.includes('90') ? 91 : bucket.includes('60') ? 61 : bucket.includes('30') ? 31 : 0)}
                    sx={{ height: 10, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {totalAP > 0 ? ((amount / totalAP) * 100).toFixed(1) : 0}% of total
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Outstanding Bills" />
          <Tab label="Vendor Balances" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Outstanding Bills
              </Typography>
              <TextField
                size="small"
                placeholder="Search bills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'doc_number'}
                        direction={sortBy === 'doc_number' ? sortDirection : 'asc'}
                        onClick={() => handleSort('doc_number')}
                      >
                        Bill #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'vendor_name'}
                        direction={sortBy === 'vendor_name' ? sortDirection : 'asc'}
                        onClick={() => handleSort('vendor_name')}
                      >
                        Vendor
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'txn_date'}
                        direction={sortBy === 'txn_date' ? sortDirection : 'asc'}
                        onClick={() => handleSort('txn_date')}
                      >
                        Bill Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'due_date'}
                        direction={sortBy === 'due_date' ? sortDirection : 'asc'}
                        onClick={() => handleSort('due_date')}
                      >
                        Due Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'total_amt'}
                        direction={sortBy === 'total_amt' ? sortDirection : 'asc'}
                        onClick={() => handleSort('total_amt')}
                      >
                        Total
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'balance'}
                        direction={sortBy === 'balance' ? sortDirection : 'asc'}
                        onClick={() => handleSort('balance')}
                      >
                        Balance
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBills.map((bill, index) => {
                    const daysOverdue = calculateDaysOverdue(bill.due_date);
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {bill.doc_number || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{bill.vendor_ref_name || bill.vendor_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={bill.entity_name || bill.company_name || '-'}
                            size="small"
                            sx={{
                              backgroundColor: getEntityColor(bill.entity_name || bill.company_name),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(bill.txn_date)}</TableCell>
                        <TableCell>
                          <Tooltip title={formatRelativeDate(bill.due_date)}>
                            <span>{formatDate(bill.due_date)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrencyPrecise(bill.total_amount || bill.total_amt, reportingCurrency)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {formatCurrencyPrecise(bill.balance, reportingCurrency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={daysOverdue > 0 ? `${daysOverdue}d overdue` : 'Current'}
                            size="small"
                            color={getAgingColor(daysOverdue)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredBills.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No outstanding bills found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredBills.length > 50 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Showing 50 of {filteredBills.length} bills
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Vendor Balances
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell align="right">Open Bills</TableCell>
                    <TableCell align="right">Total Balance</TableCell>
                    <TableCell align="right">Overdue Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendorBalances.map((vendor, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {vendor.vendor_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.entity_name || vendor.company_name || '-'}
                          size="small"
                          sx={{
                            backgroundColor: getEntityColor(vendor.entity_name || vendor.company_name),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{vendor.bill_count || vendor.open_bills || 0}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {formatCurrencyPrecise(vendor.total_balance || vendor.balance, reportingCurrency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight={600}
                          color={(vendor.overdue_amount || 0) > 0 ? 'error.main' : 'text.secondary'}
                        >
                          {formatCurrencyPrecise(vendor.overdue_amount || 0, reportingCurrency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {vendorBalances.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No vendor balances found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
